import { useAppDispatch, useAppSelector } from "../../../hooks";
import {
  selectOverviewDomainsRowsTop,
  selectOverviewSourceUsageChart,
  selectOverviewSourceUsageShowSkeleton,
  selectOverviewDomainsError,
} from "../../../store/selectors/overview";
import {
  selectLastUpdateTimeByViewBrandIdState,
  selectOverviewScoresBrandsByDateState,
  selectOverviewScoresFromDailyLoadingState,
  selectOverviewTopModelState,
  selectOverviewTopModelLoadingState,
  selectOverviewTopModelLoadCompleteState,
  selectOverviewScoreForViewBrand,
} from "../../../store/selectors/scores";
import { setSelectedModelId } from "../../../store/overview/slice";
import { normalizeOverviewRankingList } from "../utils/normalizeOverviewRankingList";
import { normalizeOverviewScatterChart } from "../utils/normalizeOverviewScatterChart";
import type { OverviewPageData } from "./types";

export function useOverviewPageData(): OverviewPageData {
  const dispatch = useAppDispatch();
  const scores = useAppSelector(selectOverviewScoresBrandsByDateState);
  const selectedBrandScore = useAppSelector(selectOverviewScoreForViewBrand);
  const overviewScoresLoading = useAppSelector(selectOverviewScoresFromDailyLoadingState);
  const isLoading =
    overviewScoresLoading === "loading" || overviewScoresLoading === "idle";
  const lastUpdateTime = useAppSelector(selectLastUpdateTimeByViewBrandIdState);
  const topModel = useAppSelector(selectOverviewTopModelState);
  const topModelLoading = useAppSelector(selectOverviewTopModelLoadingState) === "loading";
  const topModelLoadComplete = useAppSelector(selectOverviewTopModelLoadCompleteState);
  const domainsRowsTop = useAppSelector(selectOverviewDomainsRowsTop);
  const chart = useAppSelector(selectOverviewSourceUsageChart);
  const showSourceUsageSkeleton = useAppSelector(selectOverviewSourceUsageShowSkeleton);
  const sourceUsageError = useAppSelector(selectOverviewDomainsError);

  const rankingItems = scores ? normalizeOverviewRankingList(scores) : null;
  const scatterChartData = scores ? normalizeOverviewScatterChart(scores) : null;

  const visibility = selectedBrandScore?.visibility;
  const sentiment = selectedBrandScore?.sentiment;
  const ranking = selectedBrandScore?.ranking;

  const topSource =
    domainsRowsTop.length > 0
      ? {
          name: domainsRowsTop[0].name,
          logoUrl: domainsRowsTop[0].logoUrl,
          detailHref: domainsRowsTop[0].detailHref,
        }
      : null;

  const onTopModelClick = (modelId: number) => {
    dispatch(setSelectedModelId(modelId));
  };

  return {
    lastUpdateTime,
    visibility,
    sentiment,
    ranking,
    isLoading,
    topModel: topModel ?? null,
    topModelLoading,
    topModelLoadComplete,
    onTopModelClick,
    topSource,
    showSourceUsageSkeleton,
    sourceUsageError,
    rankingItems,
    scatterChartData,
    domainsRowsTop,
    chartData: chart.data,
    chartSeries: chart.series,
  };
}
